import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, makeStyles, CircularProgress, Avatar, Checkbox } from '@material-ui/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ConnectorData, EntReferralOption, Role } from '../../types/types';
import { StyledText } from '../StyledText';
import Form from '../Form';
import Button from '../Button';
import TextInput from '../TextInput';
import ContactSelect from '../ContactSelect';
import { useUser } from '../../contexts';
import api from '../../api/api';

const useDialogStyles = makeStyles(() => ({
  root: {
    margin: 0,
    '& .MuiPaper-root': {
      minWidth: 575,
      minHeight: 400
    },
    '& .MuiDialogTitle-root': {
      padding: '36px 24px 18px 24px'
    },
    '& .MuiDialogContent-root': {
      padding: '0 24px 0 24px'
    },
    '& .MuiDialogActions-root': {
      padding: '24px',
      display: 'flex',
      justifyContent: 'space-between'
    }
  },
  referralOptionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '10px'
  }
}));

const validationSchema = Yup.object().shape({
  email: Yup.string().required().trim(),
  message: Yup.string().required().trim(),
  investors: Yup.array().of(Yup.object().shape({
    contactName: Yup.string(),
    companyName: Yup.string(),
    matched: Yup.boolean(),
    profileImage: Yup.string().nullable(),
    investorProfileId: Yup.number()
  })).min(1),
  isCorporateReferralChecked: Yup.boolean().required()
});
interface ConnectorMessageDialogProps {
  open: boolean
  connectorData: ConnectorData
  preselectedReferral?: number
  handleClose: () => void
  handleSubmit: (values: any) => void
}

const ConnectorMessageDialog: React.FC<ConnectorMessageDialogProps> = ({
  open, connectorData, preselectedReferral, handleClose, handleSubmit
}) => {
  const styles = useDialogStyles();

  const { getAccessToken, userInfo } = useUser();

  const entId = useMemo(() => {
    if (userInfo && userInfo.role === Role.Ent) {
      return userInfo.profileId;
    }
    return undefined;
  }, [userInfo]);


  const submitFormAndReset = (values: any) => {
    handleSubmit(values);
  };

  const [referralOptions, setReferralOptions] = useState<EntReferralOption[]>([]);

  useEffect(() => {
    const fetchReferralOptions = async (id: number) => {
      const accessToken = await getAccessToken();
      const refOptions = await api.getConnectorReferralOptions(accessToken, id, connectorData.id);
      setReferralOptions(refOptions);
    };
    if (open && entId !== undefined) {
      fetchReferralOptions(entId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entId, connectorData.id]);

  useEffect(() => {
    if (referralOptions.length && preselectedReferral) {
      const found = referralOptions.find((op) => (op.investorProfileId === preselectedReferral));
      if (found) {
        formik.setFieldValue('investors', [found]);
      }
    }
    // formik dependency = infinite loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralOptions, preselectedReferral]);

  const handleContactSelectChange = (_event: any, value: EntReferralOption[], reason: any) => {
    if (reason === 'select-option' || reason === 'remove-option' || reason === 'clear') {
      formik.setFieldValue('investors', value);
    }
  };

  const formik = useFormik({
    initialValues: { email: connectorData.email, message: '', investors: [], isCorporateReferralChecked: true },
    validationSchema,
    onSubmit: submitFormAndReset,
    validateOnBlur: false,
    validateOnChange: true,
    enableReinitialize: true
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={styles}
    >
      <Form formik={formik}>
        <DialogTitle disableTypography>
          <StyledText variant='h3'>Message to Connector</StyledText>
        </DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <StyledText variant='h5' color='textSecondary'>To</StyledText>
            <Avatar src={connectorData.profileImage} style={{ marginLeft: '20px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '10px' }}>
              <StyledText color='textPrimary'>{`${connectorData.firstName} ${connectorData.lastName}`}</StyledText>
              <StyledText>{connectorData.companyName}</StyledText>
            </div>
          </div>
          <div className={styles.referralOptionsContainer}>
            <ContactSelect onChange={handleContactSelectChange} id='investors' label='Refer me to' value={formik.values.investors} menuItems={referralOptions} onBlur={formik.handleBlur} showCompany={false} />
          </div>
          <TextInput
            minWidth='316px'
            type='text'
            fullWidth
            id='message'
            multiline
            rows={5}
            label='Message'
            placeholder={`Hello ${connectorData.firstName}, I think I'd be a good match for the selected investors because...`}
            autoFocus
          />
          <div style={{ display: 'flex' }}>
            <Checkbox id='corporateReferral' checked={formik.values.isCorporateReferralChecked} onChange={(event) => { formik.setFieldValue('isCorporateReferralChecked', event.target.checked); }} />
            <StyledText variant='subtext2' style={{ marginTop: '20px' }}>Capital Raise can match me with service partners that are a good fit for my business.</StyledText>
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant='text' onClick={handleClose}>Cancel</Button>
          {formik.isSubmitting ? <CircularProgress />
            : (
              <Button
                type='submit'
                variant='contained'
                disableElevation
                color='primary'
                size='large'
                disabled={!formik.isValid || formik.isSubmitting}
              >
                Send
              </Button>
            )}
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export default ConnectorMessageDialog;
